/**
 * "AI-generated · See how" link for a gate page section: shows the latest LLM run behind a
 * step (calls, model, time) and opens that run on the AI Usage page, where every prompt and
 * response can be read. Renders nothing when there is no recorded run for the step.
 * Opens in the same tab: the login lives in sessionStorage, which a new tab does not share.
 */
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { fetchLlmProvenance } from "../services/observabilityClient";

const FONT = "Lato, sans-serif";
const CACHE_MS = 10 * 1000;
/** projectId → { at, promise }: sections on one page share one request. */
const cache = new Map();

function loadProvenance(projectId, refreshKey) {
  const key = `${projectId}`;
  const hit = cache.get(key);
  if (hit && hit.refreshKey === refreshKey && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const promise = fetchLlmProvenance(projectId)
    .then((res) => (res?.success ? res.data.steps || {} : {}))
    .catch(() => ({}));
  cache.set(key, { at: Date.now(), refreshKey, promise });
  return promise;
}

/** "gpt-4o-mini-2024-07-18" → "gpt-4o-mini" */
const shortModel = (m) => String(m).replace(/-\d{4}-\d{2}-\d{2}$/, "");

function formatWhen(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * @param {object} props
 * @param {number|string} props.projectId
 * @param {string[]} props.steps  AI Usage step names, e.g. ["Risk register"]; the newest run among them is shown
 * @param {"run"|"gate"} [props.target]  "gate" opens the project's gate section instead of one run (steps with one run per item, like Gate 2 scoring)
 * @param {any} [props.refreshKey]  change it after a (re)generation so the link points at the new run
 */
export default function AiProvenanceLink({ projectId, steps, target = "run", refreshKey, sx }) {
  const [info, setInfo] = useState(null);
  const stepsKey = (steps || []).join("|");

  useEffect(() => {
    if (!projectId || !stepsKey) return undefined;
    let alive = true;
    loadProvenance(projectId, refreshKey).then((all) => {
      if (!alive) return;
      const found = stepsKey
        .split("|")
        .map((s) => all[s])
        .filter((s) => s?.latest)
        .sort((a, b) => (a.latest.started_at < b.latest.started_at ? 1 : -1));
      setInfo(
        found.length
          ? {
              ...found[0],
              runs: found.reduce((n, s) => n + s.runs, 0),
              calls: found.reduce((n, s) => n + (s.calls || 0), 0),
            }
          : null
      );
    });
    return () => {
      alive = false;
    };
  }, [projectId, stepsKey, refreshKey]);

  if (!info) return null;
  const { latest, gate, runs, calls } = info;
  const href =
    target === "gate"
      ? `/ai-usage?project=${encodeURIComponent(projectId)}&gate=${encodeURIComponent(gate)}`
      : `/ai-usage?project=${encodeURIComponent(projectId)}&run=${encodeURIComponent(latest.run_id)}`;
  const models = latest.models.map(shortModel).filter((m, i, all) => all.indexOf(m) === i).join(", ");
  const summary =
    target === "gate"
      ? `${calls} LLM call${calls === 1 ? "" : "s"} · ${runs} run${runs === 1 ? "" : "s"}`
      : `${latest.calls} LLM call${latest.calls === 1 ? "" : "s"}${models ? ` · ${models}` : ""} · ${formatWhen(latest.started_at)}`;

  return (
    <Tooltip
      title="Open the AI Usage page to see every prompt sent to the AI and every response it gave for this section"
      arrow
    >
      <Box
        component={RouterLink}
        to={href}
        data-testid="ai-provenance-link"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          px: "10px",
          py: "3px",
          borderRadius: 999,
          bgcolor: "#F3EEFF",
          color: "#5B21B6",
          fontFamily: FONT,
          fontSize: 12,
          lineHeight: "18px",
          fontWeight: 600,
          textDecoration: "none",
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: "#E9DFFF", textDecoration: "underline" },
          ...sx,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
        <span>AI-generated</span>
        <Box component="span" sx={{ fontWeight: 400, color: "#6D5BA8" }}>
          · {summary} ·
        </Box>
        <span>See how</span>
        <ArrowForwardIcon sx={{ fontSize: 13 }} />
      </Box>
    </Tooltip>
  );
}
